import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categoryData, allCategorySlugs } from '@/mocks/categoryData';
import { getWebPageSchema, getBreadcrumbSchema } from '@/components/base/SchemaOrg';

const tintMap: Record<string, string> = {
  pink: 'bg-primary-50',
  mint: 'bg-accent-50',
  gray: 'bg-secondary-50',
  amber: 'bg-accent-100',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !allCategorySlugs.includes(slug)) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">分类不存在</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const cat = categoryData[slug];
  const pageTitle = `${cat.name} - 51吃瓜app | 最新${cat.name}爆料`;
  const pageDesc = cat.description;
  const pageUrl = 'http://shlvtian.cn/';

  // Build article links: first 2 articles use real article IDs if available, rest use article IDs from their own category
  const articleLinks = cat.articles.map((article) => {
    const globalArticleId = `article-${article.id}`;
    return globalArticleId;
  });

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(getWebPageSchema(pageTitle, pageDesc, pageUrl)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            getBreadcrumbSchema([
              { name: '首页', url: 'http://shlvtian.cn/' },
              { name: cat.name, url: pageUrl },
            ])
          ),
        }}
      />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: cat.name }]} />

        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
              <div className="lg:w-[35%] flex flex-col justify-center">
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-3">
                  {cat.name}
                </h1>
                <p className="text-lg md:text-xl font-heading italic text-foreground-700 mb-4">
                  最新最全的{cat.name}
                </p>
                <p className="text-sm text-foreground-500 leading-relaxed mb-5">
                  {cat.description}
                </p>
                <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap w-fit cursor-pointer">
                  关注此分类
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line text-sm"></i>
                  </div>
                </span>
              </div>

              <div className="lg:w-[35%]">
                <div className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden">
                  <img
                    src={cat.heroImage}
                    alt={`${cat.name} - 51吃瓜app`}
                    title={`${cat.name}热门爆料 - 51吃瓜app`}
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                </div>
              </div>

              <div className="lg:w-[30%]">
                <div className="grid grid-cols-2 gap-3">
                  {cat.articles.slice(0, 3).map((article) => (
                    <Link
                      key={article.id}
                      to="/"
                      className="block group cursor-pointer"
                    >
                      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          title={`${article.title} - 51吃瓜app`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[10px] bg-background-50/90 text-foreground-900 rounded-md">
                          {cat.name}
                        </span>
                      </div>
                    </Link>
                  ))}
                  <Link
                    to="/"
                    className="block group cursor-pointer"
                  >
                    <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
                      <img
                        src={cat.heroImage}
                        alt={`${cat.name}更多内容`}
                        title={`查看更多${cat.name} - 51吃瓜app`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <span className="absolute bottom-1.5 left-1.5 px-2 py-0.5 text-[10px] bg-background-50/90 text-foreground-900 rounded-md">
                        更多
                      </span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-8 md:py-12 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {cat.name}
                  <span className="text-primary-500 ml-2">热榜</span>
                </h2>
                <p className="text-sm text-foreground-500 mt-1">实时热度排行，持续更新</p>
              </div>
              <span className="hidden sm:inline-flex px-3 py-1.5 text-xs bg-secondary-200 text-secondary-700 rounded-full whitespace-nowrap">
                {cat.name}
              </span>
            </div>

            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x">
              {cat.articles.map((article) => (
                <Link
                  key={article.id}
                  to="/"
                  className="flex-shrink-0 w-[240px] md:w-[280px] snap-start group cursor-pointer"
                >
                  <div className="bg-background-50 rounded-xl overflow-hidden">
                    <div className="relative w-full aspect-square overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        title={`${article.title} - 51吃瓜app`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3 md:p-4">
                      <p className="text-xs text-foreground-500 line-clamp-1 mb-1">{cat.name}</p>
                      <p className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </p>
                      <div className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-fire-fill text-accent-500 text-xs"></i>
                        </div>
                        <span className="text-lg font-bold text-foreground-900">
                          {(article.hotScore / 10000).toFixed(1)}万
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <p className="text-xs font-heading text-foreground-500 uppercase tracking-wider mb-2">Related Topics</p>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">
                {cat.name}
                <br />
                <span className="italic font-heading">热门专题</span>
              </h2>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {cat.gridItems.map((item) => (
                <Link
                  key={item.id}
                  to="/"
                  className="group cursor-pointer"
                >
                  <div className="rounded-2xl overflow-hidden">
                    <div className={`relative w-full aspect-[3/4] overflow-hidden ${tintMap[item.bgTint] || 'bg-background-100'}`}>
                      <img
                        src={item.image}
                        alt={item.title}
                        title={`${item.title} - 51吃瓜app`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3 md:p-4 bg-background-50">
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{item.subtitle}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full h-[420px] md:h-[500px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20atmospheric%20abstract%20background%20with%20soft%20gradient%20of%20coral%20and%20amber%20tones%2C%20dreamy%20bokeh%20effect%2C%20warm%20golden%20light%20particles%2C%20modern%20aesthetic%2C%20soft%20diffused%20glow%2C%20abstract%20art%20photography&width=1920&height=800&seq=cta-cat-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/40 via-foreground-900/30 to-foreground-900/50"></div>
          <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-3 tracking-wide uppercase">
              更多{cat.name}尽在App
            </h2>
            <p className="text-sm md:text-base text-background-50/80 mb-2">
              下载51吃瓜app，海量{cat.name}等你来看
            </p>
            <p className="text-sm md:text-base text-background-50/80 mb-6 md:mb-8">
              支持iOS和Android，完全免费
            </p>
            <a
              href="#"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 text-sm font-medium rounded-full hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <div className="w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-download-line text-background-50 text-xs"></i>
                </div>
              </div>
              <span>立即下载 App</span>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </div>
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}